import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import SiteFooter from "@/components/SiteFooter";
import DeductionFairnessCheck from "@/components/DeductionFairnessCheck";
import EmbedSnippetBlock from "@/components/EmbedSnippetBlock";
import { Calculator, ShieldCheck, Sparkles, ArrowRight } from "lucide-react";

/**
 * Public landing page for the "Is this deduction fair?" calculator.
 *
 * Top-of-funnel surface. Anyone (signed in or not) can run the calculator
 * here. The shareable card carries a TenantTrail watermark and the URL
 * tenanttrail.net, so any screenshot or Facebook share pulls visitors
 * back to this page. We do NOT gate the calculator behind signup, the
 * conversion play is the moment of realisation that the bond claim is
 * unfair, which leads naturally into "start a free case to record the
 * dispute" below the fold.
 */
export default function CheckADeduction() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Is this bond deduction fair? Free check for Australian renters";

    const description = "Free calculator for Australian renters. Estimate the fair depreciated value of any bond-deduction item using ATO-based useful-life figures. Carpet, oven, blinds, paint and more.";
    const canonical = "https://tenanttrail.net/check-a-deduction";
    const ogImage = "https://tenanttrail.net/api/og/check-a-deduction.png";
    const title = document.title;

    // Track meta nodes we touched so we can restore them on unmount. For
    // most page-level OG tags the default index.html already has a node;
    // we override the content rather than creating duplicates, and revert
    // on unmount so the home page meta is restored.
    const upsertMeta = (attr, name, content) => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      const created = !el;
      let prev = null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      } else {
        prev = el.getAttribute("content");
      }
      el.setAttribute("content", content);
      return { el, created, prev };
    };

    const restorers = [
      upsertMeta("name", "description", description),
      upsertMeta("property", "og:title", title),
      upsertMeta("property", "og:description", description),
      upsertMeta("property", "og:image", ogImage),
      upsertMeta("property", "og:url", canonical),
      upsertMeta("property", "og:type", "website"),
      upsertMeta("name", "twitter:title", title),
      upsertMeta("name", "twitter:description", description),
      upsertMeta("name", "twitter:image", ogImage),
    ];

    // Add rel=canonical (single source of truth for search engines)
    let canon = document.querySelector('link[rel="canonical"]');
    const canonCreated = !canon;
    let canonPrev = null;
    if (!canon) {
      canon = document.createElement("link");
      canon.setAttribute("rel", "canonical");
      document.head.appendChild(canon);
    } else {
      canonPrev = canon.getAttribute("href");
    }
    canon.setAttribute("href", canonical);

    return () => {
      document.title = prevTitle;
      restorers.forEach(({ el, created, prev }) => {
        if (created && el && el.parentNode) el.parentNode.removeChild(el);
        else if (el && prev !== null) el.setAttribute("content", prev);
      });
      if (canonCreated && canon.parentNode) canon.parentNode.removeChild(canon);
      else if (canon && canonPrev !== null) canon.setAttribute("href", canonPrev);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF7F0]">
      <Navbar />
      <main className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 py-12 sm:py-16">

        {/* Hero */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-white border border-[#1B6C62]/30 mb-5">
            <Calculator size={14} strokeWidth={1.75} className="text-[#1B6C62]" />
            <span className="label-mono text-[#1B6C62]">Free for renters</span>
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#0D2B52] mb-4 leading-tight">
            Is this bond deduction fair?
          </h1>
          <p className="text-[#6B7280] max-w-2xl mx-auto leading-relaxed text-lg">
            Agents often claim full replacement cost for items that are already half worn out. This calculator uses ATO effective-life figures to show what an item is actually worth at its age. Free. No signup. No personal details collected.
          </p>
        </div>

        {/* Trust band */}
        <div className="max-w-3xl mx-auto mb-8 bg-[#FAF9F6] border-l-4 border-l-[#1B6C62] px-5 py-4">
          <p className="font-serif text-[15px] text-[#1A1A1A] leading-relaxed">
            Carpet, dishwasher, oven, blinds, ceiling fan or paint. Pick the item, enter its age and original cost. See whether the bond claim is in a reasonable ballpark. The figures are common estimates from the ATO effective-life schedule. They are a guide, not a fixed rule.
          </p>
          <p className="font-serif text-[12.5px] text-[#6B7280] italic leading-snug mt-3" data-testid="public-deduction-waiver">
            Use at your own risk. This is a free guide, not legal or financial advice. TenantTrail makes no warranty as to the accuracy of any number this tool produces and accepts no responsibility for any loss, decision or outcome from its use. See our <Link to="/disclaimer" className="underline hover:text-[#0D2B52]">full disclaimer</Link> for the complete picture.
          </p>
        </div>

        {/* The calculator. Public URL becomes the share target. */}
        <div className="mb-12" data-testid="public-deduction-section">
          <DeductionFairnessCheck shareUrl="https://tenanttrail.net/check-a-deduction" />
        </div>

        {/* Conversion CTA */}
        <div className="bg-[#0D2B52] text-white p-8 sm:p-10 text-center" data-testid="public-deduction-cta">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 mb-4">
            <Sparkles size={13} strokeWidth={1.75} />
            <span className="font-mono text-[11px] uppercase tracking-[0.12em]">Next step</span>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl mb-3 leading-tight">
            Found a deduction that does not add up?
          </h2>
          <p className="text-white/80 mb-6 max-w-xl mx-auto leading-relaxed">
            Start a free TenantTrail case. Upload the entry and exit reports, the bond claim and any photos. Move-Out Mode runs the same maths across every item, flags unlawful demands under your state law and prepares an evidence pack you can lodge at the tribunal.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/register"
              className="inline-flex items-center gap-2 bg-[#1B6C62] hover:brightness-95 text-white px-7 py-2.5 transition-all font-medium"
              data-testid="public-deduction-cta-register"
            >
              Start a free case
              <ArrowRight size={15} strokeWidth={1.75} />
            </Link>
            <Link
              to="/features"
              className="inline-flex items-center gap-2 border border-white/30 hover:bg-white/10 text-white px-6 py-2.5 transition-all"
              data-testid="public-deduction-cta-features"
            >
              See what TenantTrail does
            </Link>
          </div>
        </div>

        {/* Reassurance / how it works */}
        <div className="grid sm:grid-cols-3 gap-6 mt-12 mb-12">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck size={15} strokeWidth={1.75} className="text-[#1B6C62]" />
              <h3 className="font-serif text-lg text-[#0D2B52]">No personal details</h3>
            </div>
            <p className="text-[13.5px] text-[#6B7280] leading-relaxed">
              The shareable card holds only the item, the maths and the disclaimer. Your address, agent or photos never appear.
            </p>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Calculator size={15} strokeWidth={1.75} className="text-[#1B6C62]" />
              <h3 className="font-serif text-lg text-[#0D2B52]">Straight-line maths</h3>
            </div>
            <p className="text-[13.5px] text-[#6B7280] leading-relaxed">
              fair value equals original cost times one minus age over useful life. Past its useful life means depreciated value is zero.
            </p>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={15} strokeWidth={1.75} className="text-[#1B6C62]" />
              <h3 className="font-serif text-lg text-[#0D2B52]">Editable defaults</h3>
            </div>
            <p className="text-[13.5px] text-[#6B7280] leading-relaxed">
              Useful-life figures are common ATO estimates. A tribunal is not bound by them. Change the number if you have a better one.
            </p>
          </div>
        </div>

        {/* Embed-on-your-site block */}
        <EmbedSnippetBlock />

        <SiteFooter />
      </main>
    </div>
  );
}
